import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import {
  $createLineBreakNode, $createParagraphNode, $createTextNode, $getRoot,
  $getSelection, $isElementNode, $isRangeSelection, $isTextNode, $createRangeSelection, $setSelection,
  COMMAND_PRIORITY_CRITICAL, DROP_COMMAND, KEY_DOWN_COMMAND, PASTE_COMMAND,
} from 'lexical';
import { HashtagNode } from '@lexical/hashtag';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { FormattingToolbar, richComposerNodes, $composerHtml, $setComposerHtml, $insertComposerHtml, validComposerLink } from './driscoll_formatting';

// Existing plain drafts still preserve exact newlines and UTF-16 offsets.
export function $setComposerText(text) {
  const paragraph = $createParagraphNode();
  text.split('\n').forEach((line, index) => {
    if (index) paragraph.append($createLineBreakNode());
    if (line) paragraph.append($createTextNode(line));
  });
  $getRoot().clear().append(paragraph);
}

function $pointOffset(point) {
  const target = point.getNode();
  let offset = 0;
  let found = false;
  const visit = node => {
    if (found) return;
    if (node.is(target)) {
      offset += $isElementNode(node)
        ? node.getChildren().slice(0, point.offset).reduce((sum, child, index) => sum + child.getTextContent().length + ($isElementNode(child) && !child.isInline() && index < node.getChildrenSize() - 1 ? 2 : 0), 0)
        : point.offset;
      found = true;
    } else if ($isElementNode(node)) node.getChildren().forEach((child, index) => {
      visit(child);
      if (!found && $isElementNode(child) && !child.isInline() && index < node.getChildrenSize() - 1) offset += 2;
    });
    else offset += node.getTextContent().length;
  };
  visit($getRoot());
  return offset;
}

export function $composerSelection() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;
  const anchor = $pointOffset(selection.anchor), focus = $pointOffset(selection.focus);
  return { start: Math.min(anchor, focus), end: Math.max(anchor, focus) };
}

function $position(offset) {
  let remaining = Math.max(0, Math.min(offset, $getRoot().getTextContentSize()));
  const visit = parent => {
    const nodes = parent.getChildren();
    for (let index = 0; index < nodes.length; index++) {
      const node = nodes[index], size = node.getTextContent().length;
      if ($isTextNode(node) && remaining <= size) return [node.getKey(), remaining, 'text'];
      if ($isElementNode(node) && remaining <= size) return visit(node);
      if (remaining === 0) return [parent.getKey(), index, 'element'];
      remaining -= size;
      if ($isElementNode(node) && !node.isInline() && index < nodes.length - 1) {
        if (remaining < 2) return [parent.getKey(), index + 1, 'element'];
        remaining -= 2;
      }
    }
    return [parent.getKey(), nodes.length, 'element'];
  };
  return visit($getRoot());
}

export function $selectComposerRange(start, end) {
  const selection = $createRangeSelection();
  selection.anchor.set(...$position(start));
  selection.focus.set(...$position(end));
  $setSelection(selection);
}

function ComposerBridge({ props, forwardedRef }) {
  const [editor] = useLexicalComposerContext();
  const latest = useRef(props);
  latest.current = props;
  const text = useRef(props.value ?? '');
  const html = useRef(props.richHtml ?? null);
  const selection = useRef({ start: text.current.length, end: text.current.length });

  useLayoutEffect(() => {
    const removeRootListener = editor.registerRootListener(element => {
      if (element) {
        // Preserve the native-input-shaped contract used by Mastodon's compose
        // actions, without a hidden textarea or any duplicate text surface.
        Object.defineProperties(element, {
          value: { configurable: true, get: () => text.current },
          richHtml: { configurable: true, get: () => html.current },
          selectionStart: { configurable: true, get: () => selection.current.start },
          selectionEnd: { configurable: true, get: () => selection.current.end },
          setSelectionRange: { configurable: true, value: (start, end) => {
            selection.current = { start, end };
            editor.update(() => $selectComposerRange(start, end), { discrete: true });
          } },
        });
      }
      if (typeof forwardedRef === 'function') forwardedRef(element);
      else if (forwardedRef) forwardedRef.current = element;
    });
    const removeUpdateListener = editor.registerUpdateListener(({ editorState, tags }) => {
      let value, formatted;
      editorState.read(() => {
        value = $getRoot().getTextContent();
        formatted = $composerHtml(editor);
        const currentSelection = $composerSelection();
        if (currentSelection) selection.current = currentSelection;
      });
      const element = editor.getRootElement();
      // A hashtag being edited stays ordinary text; after leaving it, it is blue.
      const anchor = window.getSelection()?.anchorNode;
      element?.querySelectorAll('.dr-editor-hashtag').forEach(tag => {
        tag.classList.toggle('dr-editor-hashtag-editing', document.activeElement === element && !!anchor && tag.contains(anchor));
      });
      if (value !== text.current || formatted !== html.current) {
        const changed = value !== text.current || (html.current !== null && formatted !== html.current);
        text.current = value;
        html.current = formatted;
        if (changed && !tags.has('dr-external-value') && element) latest.current.onChange?.({ target: element, currentTarget: element });
      }
    });
    // Lexical must see Mastodon's autocomplete and Ctrl/Cmd+Enter shortcuts
    // before its own Enter/Tab handlers, not afterwards in React bubbling.
    const removeKeys = editor.registerCommand(KEY_DOWN_COMMAND, event => {
      latest.current.onKeyDown?.(event);
      if (event.defaultPrevented) return true;
      if (event.key !== 'ArrowDown' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return false;
      const caret = $getSelection();
      if (!$isRangeSelection(caret) || !caret.isCollapsed()) return false;
      let code = caret.anchor.getNode();
      while (code && code.getType() !== 'code') code = code.getParent();
      if (!code) return false;
      const prefix = $createRangeSelection();
      prefix.anchor.set(code.getKey(), 0, 'element');
      prefix.focus.set(caret.anchor.key, caret.anchor.offset, caret.anchor.type);
      // Leave only from the final code line; other Down presses stay native.
      if (code.getTextContent().slice(prefix.getTextContent().length).includes('\n')) return false;
      let paragraph = code.getNextSibling();
      if (paragraph?.getType() !== 'paragraph' || !paragraph.isEmpty()) {
        paragraph = $createParagraphNode();
        code.insertAfter(paragraph);
      }
      const nextCaret = paragraph.selectStart();
      nextCaret.setFormat(0);
      nextCaret.setStyle('');
      event.preventDefault();
      return true;
    }, COMMAND_PRIORITY_CRITICAL);
    const removePaste = editor.registerCommand(PASTE_COMMAND, event => {
      latest.current.onPaste?.(event);
      if (!event.defaultPrevented && event.clipboardData?.getData('text/html')) {
        event.preventDefault();
        $insertComposerHtml(editor, event.clipboardData.getData('text/html'));
      }
      return event.defaultPrevented;
    }, COMMAND_PRIORITY_CRITICAL);
    const removeDrop = editor.registerCommand(DROP_COMMAND, event => {
      latest.current.onDrop?.(event);
      return event.defaultPrevented;
    }, COMMAND_PRIORITY_CRITICAL);
    if (latest.current.autoFocus) editor.focus();
    return () => {
      removeRootListener(); removeUpdateListener(); removeKeys(); removePaste(); removeDrop();
      if (typeof forwardedRef === 'function') forwardedRef(null);
      else if (forwardedRef) forwardedRef.current = null;
    };
  }, [editor, forwardedRef]);

  useLayoutEffect(() => { editor.setEditable(!props.disabled); }, [editor, props.disabled]);
  useLayoutEffect(() => {
    const value = props.value ?? '';
    const imported = props.richHtml;
    if (value === text.current && (imported === html.current || !imported)) return;
    const range = selection.current;
    editor.update(() => {
      if (imported && imported !== html.current) $setComposerHtml(editor, imported);
      else if (!imported || !value) $setComposerText(value);
      else {
        // Emoji and autocomplete actions replace only their text range, rather
        // than rebuilding the document and silently losing every format.
        const old = $getRoot().getTextContent();
        let start = 0, suffix = 0;
        while (start < old.length && start < value.length && old[start] === value[start]) start++;
        while (suffix < old.length - start && suffix < value.length - start && old[old.length - 1 - suffix] === value[value.length - 1 - suffix]) suffix++;
        $selectComposerRange(start, old.length - suffix);
        $getSelection().insertText(value.slice(start, value.length - suffix));
      }
      if (document.activeElement === editor.getRootElement()) $selectComposerRange(Math.min(range.start, value.length), Math.min(range.end, value.length));
    }, { discrete: true, tag: !imported || imported !== html.current ? 'dr-external-value' : 'dr-plain-insertion' });
  }, [editor, props.value, props.richHtml]);
  return null;
}

export const DriscollEditor = forwardRef((props, forwardedRef) => {
  const [config] = useState(() => ({
    namespace: 'DriscollComposer', nodes: [HashtagNode, ...richComposerNodes], editable: !props.disabled,
    theme: { paragraph: 'dr-editor-paragraph', hashtag: 'dr-editor-hashtag', quote: 'dr-editor-quote', code: 'dr-editor-code', link: 'dr-editor-link', text: { bold: 'dr-editor-bold', italic: 'dr-editor-italic', underline: 'dr-editor-underline', strikethrough: 'dr-editor-strike', underlineStrikethrough: 'dr-editor-underline-strike', code: 'dr-editor-inline-code' }, list: { ul: 'dr-editor-list', ol: 'dr-editor-list', listitem: 'dr-editor-list-item' } },
    editorState: editor => props.richHtml ? $setComposerHtml(editor, props.richHtml) : $setComposerText(props.value ?? ''),
    onError: error => { throw error; },
  }));
  return <LexicalComposer initialConfig={config}>
    <FormattingToolbar disabled={props.disabled} />
    <div className='dr-editor-surface'>
    <RichTextPlugin ErrorBoundary={LexicalErrorBoundary} contentEditable={
      <ContentEditable className={`${props.className ?? ''} dr-editor-input`}
        aria-label={props['aria-label'] ?? props.placeholder ?? 'Post text'}
        aria-autocomplete={props['aria-autocomplete']} aria-placeholder={props.placeholder ?? ''}
        placeholder={<span className='dr-editor-placeholder'>{props.placeholder}</span>}
        dir={props.dir} lang={props.lang} spellCheck
        onFocus={props.onFocus} onBlur={props.onBlur} onKeyUp={props.onKeyUp} />
    } />
    </div>
    <HistoryPlugin />
    <ListPlugin />
    <LinkPlugin validateUrl={validComposerLink} />
    <HashtagPlugin />
    <ComposerBridge props={props} forwardedRef={forwardedRef} />
  </LexicalComposer>;
});
